import { useQuery } from "@tanstack/react-query";

import { getLocations, getUsers, getVariables, queryKeys, searchVariables, staleTimes } from "@/api";
import { useClient } from "@/hooks/useClient";
import { useDebouncedValue, useToken } from "@/hooks/core";
import { GeoFenceLocation, User, Variable } from "@/types";
import { unwrap } from "@/utils";

// Stable empty results so consumers' memos don't re-run while data loads
const NO_VARIABLES: Variable[] = [];
const NO_LOCATIONS: GeoFenceLocation[] = [];
const NO_USERS: User[] = [];

const MIN_SEARCH_LENGTH = 2;


/** All tags for the client, cached across pages */
export const useVariables = () => {
  const getToken = useToken();
  const client = useClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.variables(client),
    queryFn: async () => unwrap(await getVariables(await getToken(), client)) ?? [],
    staleTime: staleTimes.reference,
  });

  return { variables: data ?? NO_VARIABLES, loading: isLoading, error: isError };
};

export const useLocations = () => {
  const getToken = useToken();

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.locations(),
    queryFn: async () => unwrap(await getLocations(await getToken())) ?? [],
    staleTime: staleTimes.reference,
  });

  return { locations: data ?? NO_LOCATIONS, loading: isLoading, error: isError };
};

/** Users an audience or activation can be shared with */
export const useUsers = () => {
  const getToken = useToken();

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.users(),
    queryFn: async () => unwrap(await getUsers(await getToken())) ?? [],
    staleTime: staleTimes.users,
  });

  return { users: data ?? NO_USERS, loading: isLoading, error: isError };
};

/**
 * Searches tags once the term stops changing. Results are cached per term, and
 * because each term has its own query a slow earlier response can't replace a
 * later one.
 */
export const useVariableSearch = (searchText: string) => {
  const getToken = useToken();
  const client = useClient();
  const term = useDebouncedValue(searchText.trim());

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.variableSearch(client, term),
    queryFn: async () => unwrap(await searchVariables(await getToken(), term, client)) ?? [],
    enabled: term.length >= MIN_SEARCH_LENGTH,
    staleTime: staleTimes.search,
  });

  // Clearing the box hides results straight away rather than after the debounce
  const active = searchText.trim().length >= MIN_SEARCH_LENGTH;

  return {
    results: active && data ? data : NO_VARIABLES,
    loading: active && isLoading,
    error: active && isError,
  };
};
